import React, { useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, thumbUrl, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, Download, Images, History, Globe, Loader2, Sparkles, FileText, Upload, GitMerge } from "lucide-react";
import { KIND_COLOR } from "../components/KindBadge";
import { toast } from "sonner";

const CONFIGS = {
  ante: {
    endpoint: "/admin/import/ante-mortem",
    kind: "ante_mortem",
    label: "Missing persons",
    title: "Bulk import — missing persons",
     refHint: "EF-{CC}-AM####",
    desc: "Upload a CSV or XLSX of public, GDPR-cleared missing-person records. This intake template captures the full physical profile so the matching engine can compare against unidentified bodies.",
    requiredNote: "Columns (only \u201cFull name of missing person\u201d and \u201cLast seen - Country\u201d are required \u2014 the rest are optional):",
    headers: [
      "Full name of missing person", "Relation (optional)", "Contact person name", "Contact phone", "Contact email",
      "Date of birth (mm/dd/yyyy)", "Age", "Gender",
      "Height (cm)", "Weight (kg)", "Hair color", "Eye color", "Ethnicity / origin",
      "Distinguishing marks (scars, tattoos, birthmarks)", "Clothing or accessories",
      "Last seen - Country", "Last seen - City or region", "Date last seen (mm/dd/yyyy)",
      "Circumstances and additional notes", "Source organisation", "Photo URL", "Source URL",
    ],
    sample:
      "Full name of missing person,Relation (optional),Contact person name,Contact phone,Contact email,Date of birth (mm/dd/yyyy),Age,Gender,Height (cm),Weight (kg),Hair color,Eye color,Ethnicity / origin,\"Distinguishing marks (scars, tattoos, birthmarks)\",Clothing or accessories,Last seen - Country,Last seen - City or region,Date last seen (mm/dd/yyyy),Circumstances and additional notes,Source organisation,Photo URL,Source URL\n" +
      "Marie Dupont,mother,Jeanne Dupont,+33 6 12 34 56 78,jeanne.dupont@example.eu,08/02/1985,,female,158,52,blond,blue,French,Scar on left wrist,Red winter coat with fur lining,France,Lyon,07/22/2019,\"Last seen walking to the school bus stop; never arrived.\",Missing Persons EU,https://example.eu/photos/marie.jpg,https://example.eu/case/1\n",
    templateName: "eurofind-ante-mortem-template.csv",
    hasSourceOrg: true,
  },
  post: {
    endpoint: "/admin/import/post-mortem",
    kind: "post_mortem",
    label: "Unidentified people",
    title: "Bulk import — unidentified people",
    refHint: "EF-{CC}-PM####",
    desc: "Upload a partner CSV or XLSX of unidentified-body records (e.g. from a foreign authority, NGO or investigative journalist). Only COUNTRY is required per row; a reference code is generated automatically when left empty.",
    requiredNote: "Columns (only COUNTRY is required — the rest are optional):",
    headers: [
      "REFERENCE CODE", "COUNTRY", "LOCATION FOUND", "DATE FOUND", "GENDER",
      "AGE MIN", "AGE MAX", "HEIGHT MIN", "HEIGHT MAX", "WEIGHT",
      "HAIR COLOR", "EYE COLOR", "ETHNICITY", "DISTINGUISHING MARKS", "CLOTHING",
      "ESTIMATED PMI DAYS", "SOURCE ORGANISATION", "PHOTO URL", "SOURCE URL", "EXTRA POST MORTEM DATA",
    ],
    sample:
      "REFERENCE CODE,COUNTRY,LOCATION FOUND,DATE FOUND,GENDER,AGE MIN,AGE MAX,HEIGHT MIN,HEIGHT MAX,WEIGHT,HAIR COLOR,EYE COLOR,ETHNICITY,DISTINGUISHING MARKS,CLOTHING,ESTIMATED PMI DAYS,SOURCE ORGANISATION,PHOTO URL,SOURCE URL,EXTRA POST MORTEM DATA\n" +
      ",Belgium,Beach near Ostend,2021-08-14,female,14,18,155,165,,brown,blue,European,Small swallow tattoo on left ankle,Red jacket,90,CRiMSoN,https://example.eu/photos/case1.jpg,https://example.eu/case/heulmeisje,Found near the waterline\n",
    templateName: "eurofind-post-mortem-template.csv",
    hasSourceOrg: true,
  },
};

export default function AdminImportPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const isAdmin = user?.role === "admin";
  const [searchParams] = useSearchParams();
  const [kind, setKind] = useState(() => (searchParams.get("kind") === "post" ? "post" : "ante"));
  const [file, setFile] = useState(null);
  const [sourceOrg, setSourceOrg] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const cfg = CONFIGS[kind];

  const [zipFile, setZipFile] = useState(null);
  const [zipUploading, setZipUploading] = useState(false);
  const [zipResult, setZipResult] = useState(null);
  const zipInputRef = useRef(null);

  const [url, setUrl] = useState("");
  const [urlLimit, setUrlLimit] = useState(15);
  const [analysing, setAnalysing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [committing, setCommitting] = useState(false);
  const [urlResult, setUrlResult] = useState(null);

  const [pdfFiles, setPdfFiles] = useState([]);
  const [pdfAnalysing, setPdfAnalysing] = useState(false);
  const pdfInputRef = useRef(null);

  const [backfill, setBackfill] = useState(null);
  const backfillPollRef = useRef(null);

  const loadBackfillStatus = async () => {
    try {
      const { data } = await api.get("/admin/backfill-photos/status");
      setBackfill(data);
      if (data.status === "running" && !backfillPollRef.current) {
        backfillPollRef.current = setInterval(loadBackfillStatus, 3000);
      }
      if (data.status !== "running" && backfillPollRef.current) {
        clearInterval(backfillPollRef.current);
        backfillPollRef.current = null;
      }
    } catch (e) {
      // noop
    }
  };

  React.useEffect(() => {
    if (isAdmin) loadBackfillStatus();
    return () => { if (backfillPollRef.current) clearInterval(backfillPollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const startBackfill = async () => {
    try {
      await api.post("/admin/backfill-photos", { kind: "both", limit_photos: 3 });
      toast.success("Photo backfill started");
      setBackfill({ status: "running", total: 0, processed: 0, updated: 0, photos: 0 });
      if (!backfillPollRef.current) backfillPollRef.current = setInterval(loadBackfillStatus, 3000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start backfill");
    }
  };

  const switchKind = (k) => {
    setKind(k);
    setFile(null);
    setResult(null);
    setSourceOrg("");
    setZipFile(null);
    setZipResult(null);
    setPreview(null);
    setSelected(new Set());
    setUrlResult(null);
    setPdfFiles([]);
  };

  const onFile = (f) => {
    if (!f) return;
    if (!/\.(csv|xlsx|xlsm)$/i.test(f.name)) {
      toast.error("Please choose a .csv or .xlsx file");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const onUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (cfg.hasSourceOrg && sourceOrg.trim()) fd.append("source_org", sourceOrg.trim());
      const { data } = await api.post(cfg.endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.created} record(s)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Import failed");
    }
    setUploading(false);
  };

  const typeOk = (r) => !r.record_type || r.record_type === "unclear"
    || r.record_type === (cfg.kind === "ante_mortem" ? "missing_person" : "unidentified_body");

  const initSelection = (records) =>
    setSelected(new Set(records.map((r, i) => (typeOk(r) ? i : -1)).filter((i) => i >= 0)));

  const analyseUrl = async () => {
    if (!url.trim()) return;
    setAnalysing(true);
    setPreview(null);
    setUrlResult(null);
    setSelected(new Set());
    try {
      const { data } = await api.post(
        "/admin/import/url/preview",
        { url: url.trim(), kind: cfg.kind, limit: Number(urlLimit) || 15 },
        { timeout: 180000 }
      );
      setPreview(data);
      initSelection(data.records);
      if (!data.records.length) toast.info("No records could be extracted from that page.");
      else toast.success(`Found ${data.records.length} record(s)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Analysis failed");
    }
    setAnalysing(false);
  };

  const analysePdf = async () => {
    if (!pdfFiles.length) return;
    setPdfAnalysing(true);
    setPreview(null);
    setUrlResult(null);
    setSelected(new Set());
    try {
      const fd = new FormData();
      pdfFiles.forEach((f) => fd.append("files", f));
      fd.append("kind", cfg.kind);
      const { data } = await api.post("/admin/import/pdf/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      setPreview({ ...data, url: null, label: `PDF: ${data.filename}` });
      initSelection(data.records);
      const failed = (data.files || []).filter((f) => f.error);
      if (failed.length) toast.error(`${failed.length} PDF(s) failed: ${failed.map((f) => f.filename).join(", ")}`);
      if (!data.records.length) toast.info("No records could be extracted from the PDF(s).");
      else toast.success(`Found ${data.records.length} record(s) in ${(data.files || []).length} PDF(s)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Analysis failed");
    }
    setPdfAnalysing(false);
  };

  const toggleRow = (i) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const toggleAll = () => {
    if (!preview) return;
    setSelected((prev) =>
      prev.size === preview.records.length ? new Set() : new Set(preview.records.map((_, i) => i))
    );
  };

  const commitUrl = async () => {
    if (!preview || selected.size === 0) return;
    setCommitting(true);
    setUrlResult(null);
    try {
      const records = preview.records.filter((_, i) => selected.has(i));
      const body = { kind: cfg.kind, records, source_url: preview.url || null, label: preview.label || null };
      if (cfg.hasSourceOrg && sourceOrg.trim()) body.source_org = sourceOrg.trim();
      const { data } = await api.post("/admin/import/url/commit", body, { timeout: 180000 });
      setUrlResult(data);
      setPreview(null);
      setSelected(new Set());
      toast.success(`Imported ${data.created} record(s)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Import failed");
    }
    setCommitting(false);
  };

  const downloadTemplate = () => {
    const blob = new Blob([cfg.sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = cfg.templateName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onZipUpload = async () => {
    if (!zipFile) return;
    setZipUploading(true);
    setZipResult(null);
    try {
      const fd = new FormData();
      fd.append("file", zipFile);
      fd.append("kind", cfg.kind);
      const { data } = await api.post("/admin/import/photos-zip", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setZipResult(data);
      toast.success(`Linked ${data.linked} photo(s)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Photo import failed");
    }
    setZipUploading(false);
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-10" data-testid="admin-import-page">
      <p className="font-body text-xs uppercase tracking-[0.18em] text-[#6B7F82]">{t("imp.eyebrow")}</p>
      <div className="mt-1 flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-[#2E3A46] text-white">
          <FileSpreadsheet size={18} />
        </span>
        <h1 className="font-heading text-3xl font-semibold text-[#1A2024]" data-testid="import-title">{t(`imp.title.${kind}`)}</h1>
        {isAdmin && (
          <Link to="/admin/import-logs" className="ml-auto inline-flex items-center gap-1 font-body text-sm font-medium text-[#2E3A46] hover:underline" data-testid="import-history-link">
            <History size={15} /> Import history
          </Link>
        )}
      </div>

      {isAdmin && backfill && (
        <div className="mt-6 rounded-lg border border-[#4A7C59]/30 bg-[#4A7C59]/[0.05] p-5" data-testid="photo-backfill-card">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-md bg-[#4A7C59] text-white"><Images size={18} /></span>
              <div>
                <h3 className="font-heading text-base font-semibold text-[#1A2024]">Fetch missing photos</h3>
                <p className="mt-1 max-w-lg font-body text-sm text-[#5C6873]">
                  For records that have a source link but no photo, EuroFind opens the source page, lets the AI
                  pick the person's photo(s) and attaches them automatically.
                </p>
              </div>
            </div>
            {backfill.status !== "running" && (
              <Button
                onClick={startBackfill}
                className="bg-[#4A7C59] text-white hover:bg-[#3C6549]"
                disabled={backfill.status === "idle" && !backfill.candidates}
                data-testid="photo-backfill-start"
              >
                <Sparkles size={15} className="mr-1" />
                {backfill.status === "idle" && backfill.candidates
                  ? `Fetch photos for ${backfill.candidates} record(s)`
                  : "Fetch missing photos"}
              </Button>
            )}
          </div>

          {backfill.status === "running" && (
            <div className="mt-4" data-testid="photo-backfill-progress">
              <div className="flex items-center gap-2 font-body text-sm text-[#2E3A46]">
                <Loader2 size={15} className="animate-spin" />
                Processing {backfill.processed || 0} / {backfill.total || "…"} ·
                {" "}{backfill.updated || 0} with photos · {backfill.photos || 0} images stored
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#E2E8F0]">
                <div
                  className="h-full bg-[#4A7C59] transition-all"
                  style={{ width: `${backfill.total ? Math.round((backfill.processed / backfill.total) * 100) : 5}%` }}
                />
              </div>
            </div>
          )}

          {backfill.status === "done" && (
            <div className="mt-3 font-body text-sm text-[#2E3A46]" data-testid="photo-backfill-done">
              Done · {backfill.updated} record(s) got photos · {backfill.photos} image(s) stored
              {backfill.failed ? ` · ${backfill.failed} could not be processed` : ""}.
            </div>
          )}

          {backfill.status === "done" && (backfill.failures?.length > 0) && (
            <details className="mt-3" data-testid="photo-backfill-failures">
              <summary className="cursor-pointer font-body text-sm font-medium text-[#9E4747]">
                {backfill.failures.length} record(s) without a usable photo — review source links
              </summary>
              <div className="mt-2 max-h-72 space-y-1.5 overflow-auto">
                {backfill.failures.map((f, i) => (
                  <div key={i} className="rounded-md border border-[#E2E8F0] bg-white px-3 py-2" data-testid={`backfill-failure-${i}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-body text-sm font-medium text-[#1A2024]">{f.label}</span>
                      <span className="font-body text-[11px] uppercase tracking-wide text-[#6B7F82]">{f.kind === "ante_mortem" ? "Missing" : "Unidentified"}</span>
                    </div>
                    <p className="mt-0.5 font-body text-xs text-[#9E4747]">{f.reason}</p>
                    {f.source_url && (
                      <a href={f.source_url} target="_blank" rel="noreferrer" className="font-body text-xs text-[#2E3A46] underline break-all">
                        {f.source_url}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}

          {backfill.status === "idle" && !backfill.candidates && (
            <div className="mt-3 font-body text-sm text-[#5C6873]" data-testid="photo-backfill-none">
              No records are currently missing a photo with a usable source link.
            </div>
          )}
        </div>
      )}


      <div className="mt-5 inline-flex rounded-lg border border-[#E2E8F0] bg-[#F8F9FA] p-1" data-testid="import-kind-tabs">
        {Object.entries(CONFIGS).map(([k, c]) => (
          <button
            key={k}
            onClick={() => switchKind(k)}
            className={`rounded-md px-4 py-1.5 font-body text-sm transition-base ${kind === k ? "bg-white font-medium shadow-sm" : "text-[#5C6873] hover:text-[#1A2024]"}`}
            style={kind === k ? { color: KIND_COLOR[k] || "#1A2024" } : undefined}
            data-testid={`import-tab-${k}`}
          >
            {t(`imp.tab.${k}`)}
          </button>
        ))}
      </div>

      <p className="mt-4 max-w-2xl font-body text-sm text-[#5C6873]">
        {t(`imp.desc.${kind}`)}
      </p>

      {/* ---- Import from a PDF (AI) ---- */}
      <div className="mt-6 rounded-lg border border-[#E2E8F0] bg-white p-5 shadow-sm" data-testid="pdf-import-section">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-[#2E3A46]" />
          <h2 className="font-heading text-lg font-semibold text-[#1A2024]">{t("imp.pdf.title")}</h2>
          <Badge variant="outline" className="ml-1 rounded-full border-[#C99B4B]/40 bg-[#C99B4B]/10 text-[11px] text-[#8A6A2E]">Beta</Badge>
        </div>
        <p className="mt-1 max-w-2xl font-body text-sm text-[#5C6873]">{t("imp.pdf.desc")}</p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            ref={pdfInputRef}
            type="file"
            accept=".pdf,application/pdf"
            multiple
            className="hidden"
            onChange={(e) => setPdfFiles(Array.from(e.target.files || []))}
            data-testid="pdf-file-input"
          />
          <Button variant="outline" className="h-11 max-w-xs truncate border-[#E2E8F0] text-[#2E3A46]" onClick={() => pdfInputRef.current?.click()} data-testid="pdf-choose-btn">
            <Upload size={16} className="mr-2" />
            {pdfFiles.length === 0 ? t("imp.choose_file") : pdfFiles.length === 1 ? pdfFiles[0].name : `${pdfFiles.length} PDF`}
          </Button>
          <Button
            disabled={!pdfFiles.length || pdfAnalysing}
            onClick={analysePdf}
            className="h-11 bg-[#2E3A46] text-white hover:bg-[#1A2024]"
            data-testid="pdf-analyse-btn"
          >
            {pdfAnalysing ? (<><Loader2 size={16} className="mr-2 animate-spin" /> {t("imp.url.analysing")}</>) : (<><Sparkles size={16} className="mr-2" /> {t("imp.pdf.analyse")}</>)}
          </Button>
        </div>
        {pdfAnalysing && (
          <p className="mt-3 font-body text-xs text-[#6B7F82]" data-testid="pdf-analyse-hint">{t("imp.pdf.hint")}</p>
        )}
      </div>

      <div className="mt-6 rounded-lg border border-[#E2E8F0] bg-[#F8F9FA] p-5">
        <div className="flex items-center justify-between gap-3">
          <p className="font-body text-sm text-[#5C6873]">{t(`imp.required.${kind}`)}</p>
          <Button variant="outline" size="sm" className="border-[#E2E8F0] text-[#2E3A46]" onClick={downloadTemplate} data-testid="import-template-btn">
            <Download size={14} className="mr-1" /> {t("imp.template")}
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {cfg.headers.map((h) => (
            <span key={h} className="rounded-full border border-[#E2E8F0] bg-white px-2 py-0.5 font-mono text-[11px] text-[#5C6873]">{h}</span>
          ))}
        </div>
      </div>

      {cfg.hasSourceOrg && isAdmin && (
        <div className="mt-5">
          <Label className="font-body text-sm text-[#1A2024]">Source organisation (optional)</Label>
          <Input
            value={sourceOrg}
            onChange={(e) => setSourceOrg(e.target.value)}
            placeholder="e.g. CRiMSoN — applied to every row without its own SOURCE ORGANISATION"
            className="mt-1.5 h-11 border-[#E2E8F0] bg-white"
            data-testid="import-source-org-input"
          />
          <p className="mt-1 font-body text-xs text-[#6B7F82]">
            Attributes all imported cases to this partner (shown as “Submitted by …”). A per-row SOURCE ORGANISATION column overrides this.
          </p>
        </div>
      )}

      <div
        className="mt-6 rounded-lg border-2 border-dashed border-[#CBD5E1] bg-white p-10 text-center"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
        data-testid="import-dropzone"
      >
        <UploadCloud className="mx-auto text-[#6B7F82]" size={28} />
        <p className="mt-3 font-body text-sm text-[#5C6873]">
          {file ? <span className="font-medium text-[#1A2024]">{file.name}</span> : t("imp.drag")}
        </p>
        <div className="mt-3">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.xlsx,.xlsm"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
            data-testid="import-file-input"
          />
          <Button variant="outline" className="border-[#E2E8F0] text-[#2E3A46]" onClick={() => inputRef.current?.click()} data-testid="import-choose-btn">
            {t("imp.choose_file")}
          </Button>
        </div>
      </div>

      <div className="mt-5">
        <Button disabled={!file || uploading} onClick={onUpload} className="bg-[#2E3A46] text-white hover:bg-[#1A2024]" data-testid="import-upload-btn">
          {uploading ? t("imp.importing") : t("imp.import_records")}
        </Button>
      </div>

      {/* ---- Import from a web page (AI) ---- */}
      <div className="mt-10 rounded-lg border border-[#E2E8F0] bg-white p-5 shadow-sm" data-testid="url-import-section">
        <div className="flex items-center gap-2">
          <Globe size={18} className="text-[#2E3A46]" />
          <h2 className="font-heading text-lg font-semibold text-[#1A2024]">{t("imp.url.title")}</h2>
          <Badge variant="outline" className="ml-1 rounded-full border-[#C99B4B]/40 bg-[#C99B4B]/10 text-[11px] text-[#8A6A2E]">Beta</Badge>
        </div>
        <p className="mt-1 max-w-2xl font-body text-sm text-[#5C6873]">
          {t("imp.url.desc")}
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="min-w-[280px] flex-1">
            <Label className="font-body text-sm text-[#1A2024]">{t("imp.url.label")}</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.police.be/…/personnes-non-identifiees"
              className="mt-1.5 h-11 border-[#E2E8F0] bg-white"
              data-testid="url-import-input"
            />
          </div>
          <div className="w-28">
            <Label className="font-body text-sm text-[#1A2024]">{t("imp.url.max")}</Label>
            <Input
              type="number" min={1} max={25}
              value={urlLimit}
              onChange={(e) => setUrlLimit(e.target.value)}
              className="mt-1.5 h-11 border-[#E2E8F0] bg-white"
              data-testid="url-import-limit"
            />
          </div>
          <Button
            disabled={!url.trim() || analysing}
            onClick={analyseUrl}
            className="h-11 bg-[#2E3A46] text-white hover:bg-[#1A2024]"
            data-testid="url-analyse-btn"
          >
            {analysing ? (<><Loader2 size={16} className="mr-2 animate-spin" /> {t("imp.url.analysing")}</>) : (<><Sparkles size={16} className="mr-2" /> {t("imp.url.analyse")}</>)}
          </Button>
        </div>
        {analysing && (
          <p className="mt-3 font-body text-xs text-[#6B7F82]" data-testid="url-analyse-hint">
            {t("imp.url.hint")}
          </p>
        )}
      </div>

      {/* ---- AI preview & commit (shared by URL and PDF import) ---- */}
      <div>
        {preview && preview.records.length > 0 && (
          <div className="mt-6" data-testid="url-preview">
            <div className="flex items-center justify-between">
              <p className="font-body text-sm text-[#5C6873]">
                <span className="font-medium text-[#1A2024]">{preview.records.length}</span> {t("imp.url.found")}
              </p>
              <button onClick={toggleAll} className="font-body text-sm font-medium text-[#2E3A46] hover:underline" data-testid="url-toggle-all">
                {selected.size === preview.records.length ? t("imp.url.deselect_all") : t("imp.url.select_all")}
              </button>
            </div>
            <div className="mt-3 space-y-2">
              {preview.records.map((r, i) => (
                <label
                  key={i}
                  className={`flex cursor-pointer gap-3 rounded-lg border p-3 transition-base ${selected.has(i) ? "border-[#2E3A46]/50 bg-[#F2F6F3]" : "border-[#E2E8F0] bg-white hover:border-[#2E3A46]/30"}`}
                  data-testid={`url-preview-row-${i}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(i)}
                    onChange={() => toggleRow(i)}
                    className="mt-1 h-4 w-4 accent-[#2E3A46]"
                    data-testid={`url-preview-check-${i}`}
                  />
                  <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-[#E2E8F0] bg-[#F8F9FA]">
                    {(r.photo_paths?.[0] || r.photo_urls?.[0] || r.photo_url) ? (
                      <img src={r.photo_paths?.[0] ? thumbUrl(r.photo_paths[0]) : (r.photo_urls?.[0] || r.photo_url)} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="grid h-full w-full place-items-center text-[#6B7F82]"><FileSpreadsheet size={16} /></div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-heading text-sm font-semibold text-[#1A2024]">
                      {cfg.kind === "ante_mortem" ? (r.full_name || "Unnamed") : (r.reference_code || "Unidentified record")}
                    </p>
                    <p className="mt-0.5 font-body text-xs text-[#5C6873]">
                      {[r.country, r.location, r.event_date, r.source_pdf].filter(Boolean).join(" · ") || "location/date unknown"}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {r.gender && r.gender !== "unknown" && <MiniTag>{r.gender}</MiniTag>}
                      {(r.age != null || r.age_min != null) && <MiniTag>age {r.age ?? r.age_min}{r.age_max && r.age_max !== r.age_min ? `-${r.age_max}` : ""}</MiniTag>}
                      {(r.height_cm || r.height_min) && <MiniTag>{r.height_cm || r.height_min} cm</MiniTag>}
                      {r.hair_color && <MiniTag>hair: {r.hair_color}</MiniTag>}
                      {!typeOk(r) && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-[#C99B4B]/50 bg-[#C99B4B]/10 px-2 py-0.5 font-body text-[11px] font-medium text-[#8A6A2E]" data-testid={`type-warn-${i}`}>
                          <AlertTriangle size={11} />
                          {cfg.kind === "ante_mortem" ? t("imp.type_warn.ante") : t("imp.type_warn.post")}
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-4">
              <Button
                disabled={selected.size === 0 || committing}
                onClick={commitUrl}
                className="bg-[#4A7C59] text-white hover:bg-[#3d6449]"
                data-testid="url-commit-btn"
              >
                {committing ? (<><Loader2 size={16} className="mr-2 animate-spin" /> {t("imp.importing")}</>) : `${t("imp.url.import_btn")} (${selected.size})`}
              </Button>
            </div>
          </div>
        )}

        {preview && preview.records.length === 0 && (
          <div className="mt-5 rounded-lg border border-[#E2E8F0] bg-[#F8F9FA] p-4 font-body text-sm text-[#5C6873]" data-testid="url-preview-empty">
            {t("imp.url.empty")}
          </div>
        )}

        {urlResult && (
          <div className="mt-5 rounded-lg border border-[#4A7C59]/30 bg-[#4A7C59]/10 p-4 font-body text-sm text-[#3d6449]" data-testid="url-import-result">
            <CheckCircle2 size={16} className="mr-1 inline" />
            {t("imp.url.imported")} <span className="font-semibold">{urlResult.created}</span>
            {urlResult.skipped > 0 && <> · {urlResult.skipped} {t("imp.skipped_lc")}</>}
            {urlResult.errors.length > 0 && <> · <span className="text-[#9E4747]">{urlResult.errors.length} {t("imp.errors_lc")}</span></>}
          </div>
        )}
      </div>

      {isAdmin && (
      <div className="mt-10 rounded-lg border border-[#E2E8F0] bg-[#F8F9FA] p-5" data-testid="zip-section">
        <div className="flex items-center gap-2">
          <Images size={18} className="text-[#2E3A46]" />
          <h2 className="font-heading text-lg font-semibold text-[#1A2024]">Attach photos (ZIP)</h2>
        </div>
        <p className="mt-1 font-body text-sm text-[#5C6873]">
          Upload a .zip of images. Each filename must match a case reference code
          (e.g. <span className="font-mono text-xs">{cfg.refHint.replace("{CC}", "BE").replace("####", "0001")}.jpg</span>).
          Photos are linked to the matching {cfg.kind === "ante_mortem" ? "missing-person" : "unidentified"} case.
          You can also put a <span className="font-mono text-xs">PHOTO URL</span> column directly in the CSV above.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            ref={zipInputRef}
            type="file"
            accept=".zip"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f && /\.zip$/i.test(f.name)) { setZipFile(f); setZipResult(null); } else if (f) toast.error("Please choose a .zip file"); }}
            data-testid="zip-file-input"
          />
          <Button variant="outline" className="border-[#E2E8F0] text-[#2E3A46]" onClick={() => zipInputRef.current?.click()} data-testid="zip-choose-btn">
            {zipFile ? zipFile.name : "Choose .zip"}
          </Button>
          <Button disabled={!zipFile || zipUploading} onClick={onZipUpload} className="bg-[#2E3A46] text-white hover:bg-[#1A2024]" data-testid="zip-upload-btn">
            {zipUploading ? "Linking…" : "Link photos"}
          </Button>
        </div>
        {zipResult && (
          <div className="mt-4 font-body text-sm text-[#5C6873]" data-testid="zip-result">
            <span className="font-medium text-[#4A7C59]">{zipResult.linked} linked</span>
            {zipResult.unmatched.length > 0 && <> · <span className="text-[#8A6A2E]">{zipResult.unmatched.length} unmatched</span></>}
            {zipResult.errors.length > 0 && <> · <span className="text-[#9E4747]">{zipResult.errors.length} errors</span></>}
            {zipResult.unmatched.length > 0 && (
              <div className="mt-2 text-xs">Unmatched filenames: {zipResult.unmatched.slice(0, 10).join(", ")}{zipResult.unmatched.length > 10 ? "…" : ""}</div>
            )}
          </div>
        )}
      </div>
      )}

      {result && (
        <div className="mt-8 rounded-lg border border-[#E2E8F0] bg-white p-6 shadow-sm" data-testid="import-result">
          <h2 className="font-heading text-lg font-semibold text-[#1A2024]">{t("imp.summary")}</h2>
          <div className="mt-4 grid grid-cols-4 gap-3">
            <div className="rounded-md border border-[#4A7C59]/30 bg-[#4A7C59]/10 p-4 text-center" data-testid="import-created">
              <div className="font-heading text-2xl font-semibold text-[#4A7C59]">{result.created}</div>
              <div className="font-body text-xs text-[#5C6873]">{t("imp.created")}</div>
            </div>
            <div className="rounded-md border border-[#2E6B8A]/30 bg-[#2E6B8A]/10 p-4 text-center" data-testid="import-merged">
              <div className="font-heading text-2xl font-semibold text-[#2E6B8A]">{(result.merged || []).length}</div>
              <div className="font-body text-xs text-[#5C6873]">{t("imp.merged")}</div>
            </div>
            <div className="rounded-md border border-[#E2E8F0] bg-[#F8F9FA] p-4 text-center" data-testid="import-skipped">
              <div className="font-heading text-2xl font-semibold text-[#1A2024]">{result.skipped}</div>
              <div className="font-body text-xs text-[#5C6873]">{t("imp.skipped")}</div>
            </div>
            <div className="rounded-md border border-[#9E4747]/30 bg-[#9E4747]/10 p-4 text-center" data-testid="import-errors-count">
              <div className="font-heading text-2xl font-semibold text-[#9E4747]">{result.errors.length}</div>
              <div className="font-body text-xs text-[#5C6873]">{t("imp.errors")}</div>
            </div>
          </div>

          {(result.merged || []).length > 0 && (
            <div className="mt-4" data-testid="import-merged-list">
              <div className="mb-2 inline-flex items-center gap-2 font-body text-sm text-[#2E6B8A]">
                <GitMerge size={16} /> {t("imp.merged_note")}
              </div>
              <ul className="space-y-1">
                {result.merged.map((m, i) => (
                  <li key={i} className="font-body text-sm text-[#5C6873]">
                    <Badge variant="outline" className="mr-2 rounded-full border-[#E2E8F0]">{t("imp.row")} {m.row}</Badge>
                    → <span className="font-medium text-[#1A2024]">{m.into}</span>
                    {(m.fields || []).length > 0 && <span className="ml-1 text-xs">(+ {m.fields.join(", ")})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.errors.length === 0 ? (
            <div className="mt-4 inline-flex items-center gap-2 font-body text-sm text-[#4A7C59]">
              <CheckCircle2 size={16} /> {t("imp.all_ok")}
            </div>
          ) : (
            <div className="mt-4">
              <div className="mb-2 inline-flex items-center gap-2 font-body text-sm text-[#9E4747]">
                <AlertTriangle size={16} /> {t("imp.some_failed")}
              </div>
              <ul className="space-y-1" data-testid="import-errors-list">
                {result.errors.map((e, i) => (
                  <li key={i} className="font-body text-sm text-[#5C6873]">
                    <Badge variant="outline" className="mr-2 rounded-full border-[#E2E8F0]">{t("imp.row")} {e.row}</Badge>{e.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MiniTag({ children }) {
  return (
    <span className="rounded-full border border-[#E2E8F0] bg-[#F8F9FA] px-2 py-0.5 font-body text-[11px] text-[#5C6873]">
      {children}
    </span>
  );
}
